import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Loader2, Clock, Users, RefreshCw, Calendar, Search, Printer, FileDown } from "lucide-react";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHIFT = { morning: "Manhã", afternoon: "Tarde", night: "Noite" };
const ROLE_LABEL = { admin: "Admin", operator: "Operador", quality: "Qualidade", shift_leader: "Chefe" };

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a6fd91e7-1ad0-4cf0-91ce-b769f9bab27a/artifacts/m0y29wqk_Sem%20T%C3%ADtulo.png";

function openTimeTrackingPDF({ scope, dateFrom, dateTo, rows }) {
  const scopeLbl = { week: "Semanal", month: "Mensal", year: "Anual" }[scope] || scope;
  const rangeStr = `${new Date(dateFrom).toLocaleDateString("pt-PT")} → ${new Date(new Date(dateTo).getTime() - 1000).toLocaleDateString("pt-PT")}`;
  const totalHours = rows.reduce((s, r) => s + (r.total_hours || 0), 0);
  const totalDays = rows.reduce((s, r) => s + (r.distinct_days || 0), 0);
  const now = new Date().toLocaleString("pt-PT");

  const rowsHtml = rows.map((r, idx) => `
    <tr>
      <td>${idx + 1}</td>
      <td><strong>${r.user_name || "-"}</strong></td>
      <td class="mono">${r.user_code || "-"}</td>
      <td>${ROLE_LABEL[r.role] || r.role || "-"}</td>
      <td>${SHIFT[r.shift] || r.shift || "-"}</td>
      <td>${r.function || "-"}</td>
      <td class="num">${r.distinct_days || 0}</td>
      <td class="num">${r.entries_count || 0}</td>
      <td class="num hrs">${(r.total_hours || 0).toFixed(2)} h</td>
      <td class="num">${r.unclosed_ins > 0 ? '<span class="badge">SIM</span>' : "-"}</td>
    </tr>
  `).join("");

  const html = `<!DOCTYPE html><html lang="pt"><head><meta charset="utf-8">
<title>Registo de Ponto ${scopeLbl}</title>
<style>
  @page { size: A4 landscape; margin: 10mm; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #1e293b; margin: 0; padding: 0; }
  .header { display:flex; justify-content:space-between; align-items:center; border-bottom:3px solid #1e3a8a; padding-bottom:10px; margin-bottom:14px; }
  .header img { height:60px; }
  .header h1 { margin:0; font-size:18px; color:#1e3a8a; }
  .header .sub { font-size:11px; color:#64748b; margin-top:4px; }
  .info { display:flex; gap:10px; margin-bottom:12px; font-size:11px; }
  .info .box { flex:1; background:#f8fafc; border:1px solid #e2e8f0; border-radius:6px; padding:8px; text-align:center; }
  .info .lbl { color:#64748b; text-transform:uppercase; font-size:9px; letter-spacing:0.5px; }
  .info .val { font-size:16px; font-weight:bold; color:#1e3a8a; margin-top:2px; }
  table { width:100%; border-collapse:collapse; font-size:10px; }
  thead { background:#1e3a8a; color:white; }
  thead th { padding:6px 5px; text-align:left; font-size:9px; text-transform:uppercase; letter-spacing:0.3px; }
  tbody td { padding:5px; border-bottom:1px solid #e2e8f0; }
  tbody tr:nth-child(even) { background:#f8fafc; }
  .mono { font-family:monospace; color:#475569; }
  .num { text-align:right; }
  .hrs { font-weight:bold; color:#059669; }
  .badge { background:#fef3c7; color:#92400e; padding:1px 6px; border-radius:3px; font-weight:bold; font-size:9px; }
  .footer { margin-top:16px; font-size:9px; color:#94a3b8; text-align:center; border-top:1px solid #e2e8f0; padding-top:8px; }
  .no-print { text-align:right; margin:10px 0; }
  @media print { .no-print { display:none; } }
</style></head><body>
<div class="no-print">
  <button onclick="window.print()" style="padding:8px 16px;background:#1e3a8a;color:white;border:none;border-radius:4px;font-size:13px;cursor:pointer">Imprimir / PDF</button>
</div>
<div class="header">
  <img src="${LOGO_URL}" alt="EP3D" />
  <div style="flex:1; text-align:center;">
    <h1>Registo de Ponto - ${scopeLbl}</h1>
    <div class="sub">Período: <strong>${rangeStr}</strong> · Gerado em ${now}</div>
  </div>
  <div style="text-align:right; font-size:10px; color:#64748b;">
    <div>EP3D ENGINEERING</div>
    <div>NIF: 517 316 188</div>
  </div>
</div>
<div class="info">
  <div class="box"><div class="lbl">Colaboradores</div><div class="val">${rows.length}</div></div>
  <div class="box"><div class="lbl">Horas Totais</div><div class="val" style="color:#059669">${totalHours.toFixed(1)} h</div></div>
  <div class="box"><div class="lbl">Dias Trabalhados</div><div class="val">${totalDays}</div></div>
  <div class="box"><div class="lbl">Média Horas/Colab.</div><div class="val" style="color:#7c3aed">${rows.length ? (totalHours / rows.length).toFixed(1) : "0"} h</div></div>
</div>
<table>
  <thead>
    <tr>
      <th>#</th><th>Colaborador</th><th>Código</th><th>Perfil</th><th>Turno</th><th>Função</th>
      <th class="num">Dias</th><th class="num">Registos</th><th class="num">Horas</th><th class="num">Em serviço</th>
    </tr>
  </thead>
  <tbody>${rowsHtml}</tbody>
</table>
<div class="footer">
  Documento gerado automaticamente pelo Sistema MES EP3D. Reservado para uso interno.
</div>
<script>setTimeout(() => window.print(), 800);</script>
</body></html>`;
  const w = window.open("", "_blank", "width=1100,height=750");
  if (w) { w.document.write(html); w.document.close(); }
}

export default function TimeTracking() {
  const navigate = useNavigate();
  const [scope, setScope] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/time-tracking/admin-summary?scope=${scope}`);
      setData(res.data);
    } catch (e) { showError(e, "Erro a carregar registos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  const rangeLabel = data ? `${new Date(data.date_from).toLocaleDateString("pt-PT")} → ${new Date(new Date(data.date_to).getTime() - 1000).toLocaleDateString("pt-PT")}` : "";

  const filtered = (data?.summary || []).filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.user_name || "").toLowerCase().includes(s) ||
           (r.user_code || "").toLowerCase().includes(s) ||
           (r.function || "").toLowerCase().includes(s);
  });

  const totals = filtered.reduce((acc, r) => ({
    hours: acc.hours + (r.total_hours || 0),
    days: acc.days + (r.distinct_days || 0),
    users: acc.users + 1,
    unclosed: acc.unclosed + (r.unclosed_ins || 0),
  }), { hours: 0, days: 0, users: 0, unclosed: 0 });

  return (
    <div className="space-y-5 p-6">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><Clock className="h-7 w-7 text-blue-600" />Registo de Ponto</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Entradas e saídas automáticas registadas no login/logout dos colaboradores · Período: <strong>{rangeLabel}</strong>
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Button
            variant="outline"
            onClick={() => openTimeTrackingPDF({ scope, dateFrom: data.date_from, dateTo: data.date_to, rows: filtered })}
            disabled={!data || filtered.length === 0}
            className="border-red-500 text-red-700 hover:bg-red-50"
            data-testid="tt-pdf"
          >
            <FileDown className="h-4 w-4 mr-2" />PDF
          </Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="tt-print"><Printer className="h-4 w-4 mr-2" />Imprimir</Button>
          <Button variant="outline" onClick={load}><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Colaboradores</div><div className="text-2xl font-bold text-blue-600">{totals.users}</div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Horas totais</div><div className="text-2xl font-bold text-emerald-600">{totals.hours.toFixed(1)}h</div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Dias/colab.</div><div className="text-2xl font-bold text-indigo-600">{totals.users ? (totals.days / totals.users).toFixed(1) : "0"}</div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Sem saída</div><div className={`text-2xl font-bold ${totals.unclosed > 0 ? "text-amber-600" : "text-slate-400"}`}>{totals.unclosed}</div></CardContent></Card>
      </div>

      {/* Tabs + Search */}
      <div className="flex justify-between items-end gap-2 border-b flex-wrap">
        <div className="flex gap-2">
          {[
            { key: "week", label: "Semanal" },
            { key: "month", label: "Mensal" },
            { key: "year", label: "Anual" },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setScope(t.key)}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-all ${scope === t.key ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              data-testid={`tt-tab-${t.key}`}
            >
              <Calendar className="inline h-4 w-4 mr-1" />
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative pb-2">
          <Search className="absolute left-2 top-2 h-4 w-4 text-slate-400" />
          <Input placeholder="Pesquisar colaborador..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 w-64" data-testid="tt-search" />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" />Colaboradores ({filtered.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map(row => (
                <button
                  key={row.user_id}
                  onClick={() => navigate(`/employees/${row.user_id}`)}
                  className="text-left bg-white border rounded-lg p-4 hover:border-blue-500 hover:shadow-md transition-all cursor-pointer group"
                  data-testid={`tt-user-${row.user_code}`}
                >
                  <div className="flex items-center gap-3">
                    {row.photo_url ? (
                      <img src={row.photo_url} alt={row.user_name} className="h-12 w-12 rounded-full object-cover border-2 border-slate-200" />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg">
                        {(row.user_name || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate group-hover:text-blue-700">{row.user_name}</div>
                      <div className="text-xs text-slate-500">
                        {row.user_code} · {SHIFT[row.shift] || row.shift || "-"} · <span className="uppercase font-bold">{ROLE_LABEL[row.role] || row.role || "-"}</span>
                      </div>
                      {row.function && <div className="text-[10px] text-slate-400 truncate">{row.function}</div>}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-1 mt-3 text-center">
                    <div>
                      <div className="text-[9px] text-slate-500 uppercase font-bold">Dias</div>
                      <div className="font-bold text-blue-600">{row.distinct_days}</div>
                    </div>
                    <div>
                      <div className="text-[9px] text-slate-500 uppercase font-bold">Horas</div>
                      <div className="font-bold text-emerald-600">{(row.total_hours || 0).toFixed(1)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] text-slate-500 uppercase font-bold">Registos</div>
                      <div className="font-bold">{row.entries_count}</div>
                    </div>
                  </div>
                  {row.unclosed_ins > 0 && (
                    <div className="mt-2 text-[10px] bg-amber-100 text-amber-800 px-2 py-1 rounded font-semibold text-center">
                      🟢 Em serviço agora
                    </div>
                  )}
                </button>
              ))}
              {filtered.length === 0 && (
                <div className="col-span-full text-center text-muted-foreground py-8">Nenhum colaborador encontrado</div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
